/**
 * React Query Client Configuration
 * 
 * Configures the QueryClient with sensible defaults for the application.
 * This includes:
 * - Default stale time and cache time
 * - Global error handling
 * - Retry logic
 * - Refetch on window focus
 */

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';

/**
 * Default query options
 */
const defaultQueryOptions = {
  queries: {
    // Stale time: how long data is considered fresh (default 0)
    // We set this to 0 to let individual hooks control staleness
    staleTime: 0,
    
    // Cache time: how long inactive queries stay in cache (default 5 minutes)
    gcTime: 5 * 60 * 1000, // 5 minutes
    
    // Retry failed requests
    retry: (failureCount: number, error: any) => {
      // Don't retry on 4xx errors (client errors)
      if (error?.status && error.status >= 400 && error.status < 500) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    
    // Retry delay with exponential backoff
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    
    // Refetch on window focus for real-time data
    refetchOnWindowFocus: true,
    
    // Refetch on reconnect
    refetchOnReconnect: true,
    
    // Don't refetch on mount if data is fresh
    refetchOnMount: true,
  },
  mutations: {
    // Retry mutations once on network errors
    retry: (failureCount: number, error: any) => {
      if (error?.status && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 1;
    },
  },
};

/**
 * Global query error handler
 */
function handleQueryError(error: unknown) {
  console.error('Query error:', error);
  
  // You can add custom error handling here, such as:
  // - Showing toast notifications
  // - Logging to error tracking service (Sentry, etc.)
  // - Redirecting to error page
  
  // Example error types to handle:
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as any).status;
    
    if (status === 401) {
      console.error('Unauthorized - user needs to log in');
      // Could redirect to login page
    } else if (status === 403) {
      console.error('Forbidden - user lacks permissions');
    } else if (status === 404) {
      console.error('Resource not found');
    } else if (status >= 500) {
      console.error('Server error - please try again later');
    }
  }
}

/**
 * Global mutation error handler
 */
function handleMutationError(error: unknown) {
  console.error('Mutation error:', error);
  
  // Similar to query errors, but for mutations
  // You might want to show different UI feedback for mutations
}

/**
 * Create and configure the QueryClient
 */
export const queryClient = new QueryClient({
  defaultOptions: defaultQueryOptions,
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
});

/**
 * Query client helper functions
 */
export const queryHelpers = {
  /**
   * Clear all queries from the cache
   */
  clearAll: () => {
    queryClient.clear();
  },
  
  /**
   * Invalidate all queries (triggers refetch)
   */
  invalidateAll: () => {
    queryClient.invalidateQueries();
  },
  
  /**
   * Remove all queries from cache
   */
  removeAll: () => {
    queryClient.removeQueries();
  },
  
  /**
   * Get cache statistics
   */
  getStats: () => {
    const cache = queryClient.getQueryCache();
    const queries = cache.getAll();
    
    return {
      totalQueries: queries.length,
      activeQueries: queries.filter(q => q.isActive()).length,
      staleQueries: queries.filter(q => q.isStale()).length,
      fetchingQueries: queries.filter(q => q.state.fetchStatus === 'fetching').length,
    };
  },
};

/**
 * Development-only helpers
 */
if (process.env.NODE_ENV === 'development') {
  // Add queryClient to window for debugging
  if (typeof window !== 'undefined') {
    (window as any).queryClient = queryClient;
    (window as any).queryHelpers = queryHelpers;
  }
}
